import React from 'react';
import { NewMenuItem } from 'ui/src/data/MenuItem';

import Image from 'ui/src/components/Image';
import PurifiedText from 'ui/src/components/PurifiedText';
import twMerge from 'utilities/src/twMerge';
import { AnimatePresence, motion } from 'motion/react';
import ButtonLink from 'ui/src/components/ButtonLink';
import ExtraContent from 'ui/src/components/Flyout/MegaMenu/ExtraContent';
import MobileMenuLinkSection from './MobileMenuLinkSection';
import useHeader from '../header/hooks/useHeader';

interface MobileMenuLinksProps {
  subMenuItem: NewMenuItem;
}

export default function MobileMenuLinks({ subMenuItem }: MobileMenuLinksProps) {
  const { mobileMenuActiveItem } = useHeader();
  const isActiveMenuLinks = subMenuItem.id === mobileMenuActiveItem;

  const firstSection = subMenuItem.sections[0];

  return (
    <AnimatePresence>
      {isActiveMenuLinks && (
        <motion.div
          layout
          key={subMenuItem.id}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.1, ease: 'easeIn' }}
          className={twMerge(
            'flex h-full w-full flex-col transition duration-200',
            'mdS:border-t-site-neutrals-200 mdS:flex-row mdS:border-t',
          )}
        >
          {/* First Section */}
          <div className={twMerge('self-stretch pl-8 pr-4', 'mdS:bg-site-neutrals-25 mdS:py-8')}>
            <div className="lgS:mx-auto lgS:w-[15.625rem] flex h-full w-[14rem] flex-col">
              {firstSection.description && (
                <PurifiedText className="text-site-neutrals-600 site-paragraph-m [&_a]:underline">
                  {firstSection.description}
                </PurifiedText>
              )}

              <div className={twMerge('mt-4 flex flex-col gap-3', 'mdS:mb-8')}>
                {firstSection.links.map((link) => (
                  <a
                    key={link.id}
                    href={link.link_url}
                    className="text-site-neutrals-900 site-paragraph-m font-medium transition duration-300 hover:underline hover:brightness-75"
                  >
                    {link.link_label}
                  </a>
                ))}
              </div>

              {subMenuItem.extraContent && (
                <div
                  className={twMerge(
                    'border-t-site-neutrals-100 mt-auto hidden gap-4 border-t pt-6',
                    'mdS:flex',
                    subMenuItem.extraContent.image.data ? '' : 'mdS:flex-col',
                  )}
                >
                  {subMenuItem.extraContent.client.data && (
                    <Image
                      src={
                        subMenuItem.extraContent.client.data.attributes.client_logos.color_logo.data.attributes.url
                      }
                      alt={subMenuItem.extraContent.client.data.attributes.client_name}
                      width={120}
                      height={120}
                      className="h-6 w-[6.5rem] object-cover"
                    />
                  )}

                  {subMenuItem.extraContent.image.data && (
                    <Image
                      src={subMenuItem.extraContent.image.data.attributes.url}
                      alt={subMenuItem.extraContent.image.data.attributes.alternativeText || ''}
                      width={100}
                      height={100}
                      className="h-16 w-16 rounded object-cover"
                    />
                  )}
                  <PurifiedText className="text-site-neutrals-900 site-paragraph-s [&_a]:underline">
                    {subMenuItem.extraContent.content}
                  </PurifiedText>
                </div>
              )}
            </div>
          </div>

          {/* Rest of the sections and CTA */}
          <div className="flex w-full flex-col gap-8 p-8">
            <div
              className={twMerge(
                'border-site-neutrals-50 flex w-full flex-col gap-8 border-b border-t py-6',
                'mdS:contents',
              )}
            >
              {subMenuItem.sections.slice(1).map((section) => (
                <MobileMenuLinkSection section={section} key={section.id} />
              ))}
            </div>

            {subMenuItem.cta && (
              <ButtonLink
                useNextLink={false}
                href={subMenuItem.cta.link_url}
                variant="Tertiary"
                variantColor="Black"
                size="Medium"
                classes="border-none text-left !justify-start"
                iconName="ArrowForward"
                iconSize="IconS"
              >
                {subMenuItem.cta.link_label}
              </ButtonLink>
            )}
          </div>

          {subMenuItem.extraContent && <ExtraContent extraContent={subMenuItem.extraContent} />}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
